import React, { createContext, ReactNode, useEffect, useState } from 'react';

import { CartArticle, CartArticleV2, useCartContext } from '@zooplus/cart-provider';

import Cart from '../../../common/services/Cart';
import { useDeferredPromise } from '../../../common/utils/customHooks';

interface PDPAddToCartContextProps {
  onAddToCart: (articleId: number, quantityToAdd: number, node: any) => void;
  redeemableBalance: number;
  quantityInCart: number;
}

const PDPAddToCartContext = createContext<PDPAddToCartContextProps>({} as PDPAddToCartContextProps);

type PDPAddToCartProviderProps = {
  children: ReactNode;
  articleId: number;
  redeemableBalance: number;
  isArticlePromoted: boolean;
  articlePoints: number;
};

export const PDPAddToCartProvider: React.FC<PDPAddToCartProviderProps> = ({
  children,
  articleId,
  redeemableBalance: fullRedeemableBalance,
  articlePoints,
  isArticlePromoted,
}: PDPAddToCartProviderProps) => {
  const { cart, cartVersion, changeArticleQuantity } = useCartContext();
  const [quantityInCart, setQuantityInCart] = useState<number>(0);
  const [redeemableBalance, setRedeemableBalance] = useState<number>(fullRedeemableBalance);
  const { defer } = useDeferredPromise<any>();

  useEffect(() => {
    window['changeArticleQuantityDeferred'] = defer().promise;
    Cart.notifyQuantityChange((eventName: string, data: never) =>
      notifyQuantityChange(eventName, data),
    );
  }, []);

  const notifyQuantityChange = (eventName: string, data: never) => {
    const { price, quantity, shopId, psId, bestPriceArticle, cartId, sellerType } = data;
    const bonusPointsArticlePromoted = isArticlePromoted
      ? 'bpArticlePromoted'
      : 'bpArticleNotPromoted';
    window['changeArticleQuantityDeferred']
      .then((resolvedValue) => {
        resolvedValue.notify(
          psId,
          shopId,
          quantity,
          price,
          cartId,
          bestPriceArticle,
          bonusPointsArticlePromoted,
          'not present',
          articlePoints,
          sellerType
        );
      })
      .catch((error) => {
        console.error('Error resolving changeArticleQuantityDeferred:', error);
      });
  };

  useEffect(() => {
    if (cart && cartVersion) {
      switch (cartVersion) {
        case 'v1':
          const articleInCart1: CartArticle = cart.articles!.find(
            (a) => a.id === articleId,
          ) as CartArticle;
          if (articleInCart1) setQuantityInCart(articleInCart1.quantity!);
          else setQuantityInCart(0);
          setRedeemableBalance(fullRedeemableBalance - cart.bonusPoints!);
          break;
        case 'v2':
          const articleInCart2: CartArticleV2 = cart.articles!.find(
            (a) => a.id === articleId,
          ) as CartArticleV2;
          if (articleInCart2) setQuantityInCart(articleInCart2.quantity!);
          else setQuantityInCart(0);
          setRedeemableBalance(fullRedeemableBalance - cart.bonusPoints!);
          break;
      }
    }
  }, [cart]);

  const onAddToCart = (articleId: number, quantityToAdd: number, node: any): void => {
    changeArticleQuantity({
      articleId: articleId,
      quantity: quantityToAdd,
      popover: { trigger: node },
    });
  };

  return (
    <PDPAddToCartContext.Provider
      value={{ redeemableBalance, quantityInCart, onAddToCart }}>
      {children}
    </PDPAddToCartContext.Provider>
  );
};

export default PDPAddToCartContext;
